import { createFileRoute } from '@tanstack/react-router';
import { format } from 'date-fns';
import { AlertTriangle, FullscreenIcon } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';

import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { useQuickSightEmbedUrl } from '@/features/quicksight/hooks/useQuickSightEmbedUrl';
import { AppPageContent } from '@/features/shared/components/AppPageContent';
import { AppPageHeader } from '@/features/shared/components/AppPageHeader';
import { UserRoles } from '@/lib/constants/auth.constants.ts';
import {
  ACADEMIC_YEAR_OPTIONS,
  DEFAULT_ACADEMIC_YEAR_VALUE,
} from '@/lib/constants/report-dates';
import { appendQueryParams, resolveDashboardId } from '@/lib/quicksight';
import { requireRole } from '@/lib/validation';

export const Route = createFileRoute('/_app/app/map_actuals_report')({
  component: RouteComponent,
  beforeLoad: requireRole([UserRoles.ADMINISTRATOR]),
});

function RouteComponent() {
  const [isLoading, setIsLoading] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const dashboardRef = useRef<HTMLDivElement>(null);

  const dashboardId = resolveDashboardId('mapActuals');
  const {
    data,
    isLoading: isEmbedLoading,
    isError: isEmbedError,
  } = useQuickSightEmbedUrl(dashboardId);

  const [selectedOptionValue, setSelectedOptionValue] = useState(
    DEFAULT_ACADEMIC_YEAR_VALUE
  );
  const selectedOption = ACADEMIC_YEAR_OPTIONS.find(
    (o) => o.value === selectedOptionValue
  )!;
  const startDate = selectedOption.start;
  const endDate = selectedOption.end;

  const quickSightSrc = useMemo(() => {
    if (!data?.embedUrl) return '';
    return appendQueryParams(data.embedUrl, {
      pStartDate: format(startDate, 'yyyy-MM-dd'),
      pEndDate: format(endDate, 'yyyy-MM-dd'),
      firstDaySchoolCalendar: format(startDate, 'yyyy-MM-dd'),
      lastDaySchoolCalendar: format(endDate, 'yyyy-MM-dd'),
    });
  }, [data?.embedUrl, startDate, endDate]);

  // Handle fullscreen change events
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(document.fullscreenElement !== null);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () =>
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  const toggleFullscreen = () => {
    if (!isFullscreen && dashboardRef.current) {
      if (dashboardRef.current.requestFullscreen) {
        dashboardRef.current.requestFullscreen();
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      }
    }
  };

  return (
    <AppPageContent>
      <AppPageHeader
        title="MAP Actuals Report"
        breadcrumbItems={[
          {
            label: 'App',
            link: { to: '/app' },
          },
          {
            label: 'MAP Actuals Report',
            link: { to: '/app/map_actuals_report' },
          },
        ]}
      />
      <div className="p-4 w-full max-w-full">
        <div className="flex justify-end">
          <Select
            value={selectedOptionValue}
            onValueChange={(value) => {
              setSelectedOptionValue(value);
              setIsLoading(true); // trigger loading state while iframe reloads
            }}
          >
            <SelectTrigger className="w-[220px]">
              <SelectValue placeholder="Select Month" />
            </SelectTrigger>
            <SelectContent>
              {ACADEMIC_YEAR_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {isEmbedError && (
          <Alert variant="destructive" className="my-4">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              Failed to load the dashboard. Please try refreshing the page.
            </AlertDescription>
          </Alert>
        )}

        <Separator className="my-4" />
        <div
          ref={dashboardRef}
          className="dashboard-container relative border rounded-md shadow-sm"
          style={{
            width: '100%',
            maxWidth: '100%',
            height: 'calc(100vh - 150px)',
            minHeight: '600px',
            overflow: 'hidden',
          }}
        >
          <button
            onClick={toggleFullscreen}
            className="absolute top-2 right-2 z-20 px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 flex items-center opacity-80 hover:opacity-100"
          >
            <FullscreenIcon className="h-5 w-5 mr-1" />

            {isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
          </button>

          {(isEmbedLoading || isLoading) && (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-100 bg-opacity-75 z-10">
              <div className="flex flex-col items-center">
                <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                <p className="mt-2 text-gray-700">Loading dashboard...</p>
              </div>
            </div>
          )}

          {quickSightSrc && (
            <iframe
              key={`${startDate.toISOString()}-${endDate.toISOString()}`}
              src={quickSightSrc}
              style={{
                border: 'none',
                width: '100%',
                height: '100%',
                overflow: 'auto',
              }}
              title="AWS QuickSight Dashboard"
              onLoad={() => setIsLoading(false)}
            />
          )}
        </div>
      </div>
    </AppPageContent>
  );
}
